import {
	CloudDownloadIcon,
	PlusSignCircleDuotoneRounded,
	PodcastIcon,
} from "@hugeicons-pro/core-duotone-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AppContainer } from "~/components/layouts/app/app-container";
import {
	FormCard,
	FormCardBody,
	FormCardFooter,
	FormCardHeader,
	FormCardHeaderSubtitle,
	FormCardHeaderTitle,
} from "~/components/ui/form-card";
import { Label } from "~/components/ui/label";
import { PageHead } from "~/components/ui/page-head";
import { RadioGroupItem } from "~/components/ui/radio-group";
import { useChannelCreateForm } from "~/forms/app/channel/create";
import { useChannelImportForm } from "~/forms/app/channel/import";
import { useTRPC } from "~/trpc/client";

export const Route = createFileRoute("/_app/manager/new/")({
	component: Page,
	async loader({ context }) {
		context.queryClient.prefetchQuery(context.trpc.parameter.appleCategories.queryOptions());
	},
});

function Page() {
	const { t } = useLingui();
	const trpc = useTRPC();

	const channelsQuery = useSuspenseQuery(trpc.channel.list.queryOptions());
	const categoriesQuery = useQuery(trpc.parameter.appleCategories.queryOptions());

	const categories = (categoriesQuery.data ?? []).flatMap((category) => [
		{
			value: category.id.toString(),
			label: category.name,
			level: 0,
		},
		...(category.children?.map((child) => ({
			value: child.id.toString(),
			label: child.name,
			search: `${category.name} - ${child.name}`,
			level: 1,
		})) ?? []),
	]);

	const hasNoChannels = channelsQuery.data.length === 0;

	const { form: createForm } = useChannelCreateForm({
		defaultValues: {
			category: "",
			title: "",
		},
	});

	const { form: importForm } = useChannelImportForm({
		defaultValues: {
			isRssSyncActivated: false,
			rssSource: "",
			title: "",
		},
	});

	return (
		<>
			<PageHead
				icon={PodcastIcon}
				title={
					hasNoChannels ? t`Welcome to Azerion Podcasts` : t`Add a new Show to Azerion Podcasts`
				}
				subtitle={`${hasNoChannels ? t`Add your first show` : t`Add another show`} — ${t`create a new podcast or import an existing one`}`}
			/>
			<AppContainer className="my-12">
				<div className="grid grid-cols-2 gap-6">
					<createForm.AppForm>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								e.stopPropagation();
								createForm.handleSubmit();
							}}
						>
							<FormCard>
								<FormCardHeader>
									<HugeiconsIcon
										icon={PlusSignCircleDuotoneRounded}
										strokeWidth={1}
										className="text-primary size-12"
									/>
									<div>
										<FormCardHeaderTitle>
											<Trans>Create a new Podcast</Trans>
										</FormCardHeaderTitle>
										<FormCardHeaderSubtitle>
											<Trans>
												Starting a new Podcast? Congratulations! You're about to embark on an
												exciting journey.
											</Trans>
										</FormCardHeaderSubtitle>
									</div>
								</FormCardHeader>
								<FormCardBody>
									<createForm.AppField
										name="title"
										children={(field) => (
											<field.FormItem className="grid gap-2">
												<field.FormLabel>
													<Trans>Podcast Title</Trans>
												</field.FormLabel>
												<field.FormControl>
													<field.FormInput className="" />
												</field.FormControl>
												<field.FormMessage />
											</field.FormItem>
										)}
									/>
									<createForm.AppField
										name="category"
										children={(field) => {
											return (
												<field.FormItem className="grid gap-2">
													<div className="flex gap-3">
														<div className="flex grow flex-col gap-2">
															<div className="flex-1">
																<div className="flex items-center justify-between">
																	<field.FormLabel>
																		<Trans>Podcast Category</Trans>
																	</field.FormLabel>
																</div>
																<div className="text-muted-foreground mt-1 text-sm">
																	<Trans>
																		Select an Apple Podcasts category which will be used for the
																		monetization of your podcast.
																	</Trans>
																</div>
															</div>
														</div>
														<field.FormCombobox
															align="end"
															className="min-w-48"
															options={categories}
															placeholder={t`Select a category`}
														/>
													</div>
													<field.FormMessage />
												</field.FormItem>
											);
										}}
									/>
									<createForm.FormErrorMessages />
								</FormCardBody>
								<FormCardFooter>
									<createForm.FormButton>
										<Trans>Create a new Podcast</Trans>
									</createForm.FormButton>
								</FormCardFooter>
							</FormCard>
						</form>
					</createForm.AppForm>

					<importForm.AppForm>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								e.stopPropagation();
								importForm.handleSubmit();
							}}
						>
							<FormCard>
								<FormCardHeader>
									<HugeiconsIcon
										icon={CloudDownloadIcon}
										strokeWidth={1}
										className="text-primary size-12"
									/>
									<div>
										<FormCardHeaderTitle>
											<Trans>Import an existing Podcast</Trans>
										</FormCardHeaderTitle>
										<FormCardHeaderSubtitle>
											<Trans>
												Ready to take your Podcast to the next level? Import your existing Podcast
												and continue your journey.
											</Trans>
										</FormCardHeaderSubtitle>
									</div>
								</FormCardHeader>
								<FormCardBody>
									<importForm.AppField
										name="rssSource"
										children={(field) => (
											<field.FormItem className="grid gap-2">
												<field.FormLabel>
													<Trans>Podcast RSS Feed URL</Trans>
												</field.FormLabel>
												<field.FormControl>
													<field.FormInput type="url" placeholder="https://website.com/feed.xml" />
												</field.FormControl>
												<field.FormMessage />
											</field.FormItem>
										)}
									/>
									<importForm.AppField
										name="title"
										children={(field) => (
											<field.FormItem className="grid gap-2">
												<field.FormLabel>
													<Trans>Podcast Title</Trans>
												</field.FormLabel>
												<field.FormControl>
													<field.FormInput placeholder="My Awesome Podcast" />
												</field.FormControl>
												<field.FormDescription>
													<Trans>Keep this empty to use your current podcast title.</Trans>
												</field.FormDescription>
												<field.FormMessage />
											</field.FormItem>
										)}
									/>
									<importForm.AppField
										name="isRssSyncActivated"
										children={(field) => {
											return (
												<field.FormItem className="grid gap-2">
													<field.FormLabel>
														<Trans>Import mode</Trans>
													</field.FormLabel>
													<field.FormControl>
														<field.FormRadioGroup
															className="gap-2"
															onValueChange={(value) => field.setValue(value === "on")}
														>
															<div className="border-input has-data-[state=checked]:border-primary/50 relative flex w-full items-start gap-2 rounded-md border p-4 shadow-xs outline-none">
																<RadioGroupItem
																	value="off"
																	defaultChecked={field.state.value === false}
																	checked={field.state.value === false}
																	id="manual-sync"
																	className="order-1 after:absolute after:inset-0"
																/>
																<div className="grid grow gap-2">
																	<Label htmlFor="manual-sync">
																		<Trans>Migration</Trans>
																		<span className="text-muted-foreground ml-1 text-xs leading-[inherit] font-normal">
																			<Trans>(recommended)</Trans>
																		</span>
																	</Label>
																	<p className="text-muted-foreground text-xs">
																		<Trans>
																			This option allows you to migrate your podcast from another
																			platform.
																		</Trans>
																	</p>
																	<p className="text-muted-foreground text-xs">
																		<Trans>
																			We migrate all of your existing episodes once. You can then
																			create & manage new episodes directly from our platform.
																		</Trans>
																	</p>
																</div>
															</div>
															<div className="border-input has-data-[state=checked]:border-primary/50 relative flex w-full items-start gap-2 rounded-md border p-4 shadow-xs outline-none">
																<RadioGroupItem
																	value="on"
																	defaultChecked={field.state.value === true}
																	checked={field.state.value === true}
																	id="automatic-sync"
																	className="order-1 after:absolute after:inset-0"
																/>
																<div className="grid grow gap-2">
																	<Label htmlFor="automatic-sync">
																		<Trans>Synchronization</Trans>
																	</Label>
																	<p className="text-muted-foreground text-xs">
																		<Trans>
																			This option ensures your podcast is synchronized from your RSS
																			feed. It prevents you from editing the episodes from our
																			platform. Every change must be made in your RSS feed.
																		</Trans>
																	</p>
																	<p className="text-muted-foreground text-xs">
																		<Trans>
																			In our platform you'll only be able to tweak the monetization
																			settings for each episode.
																		</Trans>
																	</p>
																</div>
															</div>
														</field.FormRadioGroup>
													</field.FormControl>
													<field.FormMessage />
												</field.FormItem>
											);
										}}
									/>
									<importForm.FormErrorMessages />
								</FormCardBody>
								<FormCardFooter>
									<importForm.FormButton>
										<Trans>Start Import</Trans>
									</importForm.FormButton>
								</FormCardFooter>
							</FormCard>
						</form>
					</importForm.AppForm>
				</div>
			</AppContainer>
		</>
	);
}
